/**
 * Thread provenance metadata embedding and reading.
 *
 * Provides functions to embed/read structured provenance metadata into
 * PNG, SVG, and HTML files directly in the browser. All metadata keys
 * use the `thread:` prefix for namespacing.
 *
 * PNG handling operates at the byte level, constructing and parsing
 * tEXt chunks with correct CRC32 checksums.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ThreadMetadata {
  version: string;
  assetUrl: string;
  projectId: string;
  artifactId: string;
  projectName?: string;
  createdAt?: string;
  createdBy?: string;
  nodePath?: string;
  contentHash?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const THREAD_BASE_URL = "https://thread.flexcompute.com/a";

export function buildAssetUrl(artifactId: string): string {
  return `${THREAD_BASE_URL}/${artifactId}`;
}

// ---------------------------------------------------------------------------
// Conversion helpers
// ---------------------------------------------------------------------------

function metaToDict(meta: ThreadMetadata): Record<string, string> {
  return {
    "thread:version": meta.version,
    "thread:asset_url": meta.assetUrl,
    "thread:project_id": meta.projectId,
    "thread:project_name": meta.projectName ?? "",
    "thread:artifact_id": meta.artifactId,
    "thread:created_at": meta.createdAt ?? "",
    "thread:created_by": meta.createdBy ?? "",
    "thread:node_path": meta.nodePath ?? "",
    "thread:hash": meta.contentHash ?? "",
  };
}

function dictToMeta(d: Record<string, string>): ThreadMetadata {
  return {
    version: d["thread:version"] ?? "",
    assetUrl: d["thread:asset_url"] ?? "",
    projectId: d["thread:project_id"] ?? "",
    artifactId: d["thread:artifact_id"] ?? "",
    projectName: d["thread:project_name"] ?? "",
    createdAt: d["thread:created_at"] ?? "",
    createdBy: d["thread:created_by"] ?? "",
    nodePath: d["thread:node_path"] ?? "",
    contentHash: d["thread:hash"] ?? "",
  };
}

// ---------------------------------------------------------------------------
// CRC32 (PNG uses CRC32/ISO-3309 over chunk type + data)
// ---------------------------------------------------------------------------

const crcTable: number[] = (() => {
  const table: number[] = new Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      if (c & 1) {
        c = 0xedb88320 ^ (c >>> 1);
      } else {
        c = c >>> 1;
      }
    }
    table[n] = c;
  }
  return table;
})();

function crc32(buf: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < buf.length; i++) {
    crc = crcTable[(crc ^ buf[i]!) & 0xff]! ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

// ---------------------------------------------------------------------------
// PNG byte-level helpers
// ---------------------------------------------------------------------------

const PNG_SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);
const IEND_TYPE = new Uint8Array([73, 69, 78, 68]); // "IEND"
const TEXT_TYPE = new Uint8Array([116, 69, 88, 116]); // "tEXt"

function readU32(view: DataView, offset: number): number {
  return view.getUint32(offset, false); // big-endian
}

function writeU32(view: DataView, offset: number, value: number): void {
  view.setUint32(offset, value, false);
}

/** Encode a string to UTF-8 bytes. */
function encodeStr(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

/** Decode UTF-8 bytes to string. */
function decodeStr(buf: Uint8Array): string {
  return new TextDecoder().decode(buf);
}

/** Read a Blob into an ArrayBuffer (works in both browser and jsdom). */
async function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === "function") {
    return blob.arrayBuffer();
  }
  // Fallback for older environments / jsdom
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Build a PNG tEXt chunk: [length(4B)][type(4B)][keyword\0value][CRC(4B)].
 */
function buildTextChunk(keyword: string, value: string): Uint8Array {
  const keyBytes = encodeStr(keyword);
  const valBytes = encodeStr(value);
  // data = keyword + \0 + value
  const dataLen = keyBytes.length + 1 + valBytes.length;
  const chunk = new Uint8Array(4 + 4 + dataLen + 4);
  const view = new DataView(chunk.buffer);

  // Length
  writeU32(view, 0, dataLen);
  // Type
  chunk.set(TEXT_TYPE, 4);
  // Keyword
  chunk.set(keyBytes, 8);
  // Null separator
  chunk[8 + keyBytes.length] = 0;
  // Value
  chunk.set(valBytes, 8 + keyBytes.length + 1);
  // CRC over type+data
  const crcData = chunk.slice(4, 4 + 4 + dataLen);
  writeU32(view, 4 + 4 + dataLen, crc32(crcData));

  return chunk;
}

/**
 * Find the byte offset of the IEND chunk in a PNG buffer.
 * Returns the offset of the IEND length field, or -1 if not found.
 */
function findIendOffset(buf: Uint8Array): number {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  let offset = 8; // skip PNG signature
  while (offset + 8 <= buf.length) {
    const chunkLen = readU32(view, offset);
    // Check chunk type at offset+4
    if (
      buf[offset + 4] === IEND_TYPE[0] &&
      buf[offset + 5] === IEND_TYPE[1] &&
      buf[offset + 6] === IEND_TYPE[2] &&
      buf[offset + 7] === IEND_TYPE[3]
    ) {
      return offset;
    }
    // Move to next chunk: 4 (length) + 4 (type) + chunkLen (data) + 4 (CRC)
    offset += 12 + chunkLen;
  }
  return -1;
}

// ---------------------------------------------------------------------------
// PNG public API
// ---------------------------------------------------------------------------

/** Embed Thread provenance metadata into a PNG Blob via tEXt chunks. */
export async function embedPngMetadata(
  blob: Blob,
  meta: ThreadMetadata,
): Promise<Blob> {
  const buf = new Uint8Array(await blobToArrayBuffer(blob));

  // Validate PNG signature
  for (let i = 0; i < 8; i++) {
    if (buf[i] !== PNG_SIGNATURE[i]) {
      throw new Error("Not a valid PNG file");
    }
  }

  const iendOffset = findIendOffset(buf);
  if (iendOffset < 0) {
    throw new Error("PNG missing IEND chunk");
  }

  // Build tEXt chunks for each metadata key
  const dict = metaToDict(meta);
  const chunks: Uint8Array[] = [];
  for (const [key, value] of Object.entries(dict)) {
    chunks.push(buildTextChunk(key, value));
  }

  // Concatenate: [before IEND] + [new chunks] + [IEND chunk]
  const before = buf.slice(0, iendOffset);
  const iendChunk = buf.slice(iendOffset);

  let totalLen = before.length + iendChunk.length;
  for (const c of chunks) totalLen += c.length;

  const result = new Uint8Array(totalLen);
  let pos = 0;
  result.set(before, pos);
  pos += before.length;
  for (const c of chunks) {
    result.set(c, pos);
    pos += c.length;
  }
  result.set(iendChunk, pos);

  return new Blob([result], { type: "image/png" });
}

/** Read Thread provenance metadata from a PNG Blob. */
export async function readPngMetadata(
  blob: Blob,
): Promise<ThreadMetadata | null> {
  const buf = new Uint8Array(await blobToArrayBuffer(blob));

  // Validate PNG signature
  for (let i = 0; i < 8; i++) {
    if (buf[i] !== PNG_SIGNATURE[i]) return null;
  }

  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const dict: Record<string, string> = {};
  let offset = 8;

  while (offset + 8 <= buf.length) {
    const chunkLen = readU32(view, offset);
    const typeBytes = buf.slice(offset + 4, offset + 8);

    // Check if this is a tEXt chunk
    if (
      typeBytes[0] === TEXT_TYPE[0] &&
      typeBytes[1] === TEXT_TYPE[1] &&
      typeBytes[2] === TEXT_TYPE[2] &&
      typeBytes[3] === TEXT_TYPE[3]
    ) {
      const data = buf.slice(offset + 8, offset + 8 + chunkLen);
      // Find null separator between keyword and value
      const nullIdx = data.indexOf(0);
      if (nullIdx >= 0) {
        const keyword = decodeStr(data.slice(0, nullIdx));
        const value = decodeStr(data.slice(nullIdx + 1));
        if (keyword.startsWith("thread:")) {
          dict[keyword] = value;
        }
      }
    }

    // Check for IEND -- stop scanning
    if (
      typeBytes[0] === IEND_TYPE[0] &&
      typeBytes[1] === IEND_TYPE[1] &&
      typeBytes[2] === IEND_TYPE[2] &&
      typeBytes[3] === IEND_TYPE[3]
    ) {
      break;
    }

    offset += 12 + chunkLen;
  }

  if (!dict["thread:version"]) return null;
  return dictToMeta(dict);
}

// ---------------------------------------------------------------------------
// SVG
// ---------------------------------------------------------------------------

/** Embed Thread provenance metadata into an SVG string. */
export function embedSvgMetadata(svg: string, meta: ThreadMetadata): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svg, "image/svg+xml");
  const root = doc.documentElement;

  // Add data attributes on root <svg>
  root.setAttribute("data-thread-asset", meta.assetUrl);
  root.setAttribute("data-thread-artifact", meta.artifactId);

  // Find or create <metadata>
  let metadataEl = root.querySelector("metadata");
  if (!metadataEl) {
    metadataEl = doc.createElementNS("http://www.w3.org/2000/svg", "metadata");
    root.insertBefore(metadataEl, root.firstChild);
  }

  // Remove old thread-provenance element
  const old = metadataEl.querySelector("thread-provenance");
  if (old) old.remove();

  // Create new thread-provenance element
  const threadEl = doc.createElement("thread-provenance");
  const dict = metaToDict(meta);
  for (const [key, value] of Object.entries(dict)) {
    const child = doc.createElement(key.replace(":", "-"));
    child.textContent = value;
    threadEl.appendChild(child);
  }
  metadataEl.appendChild(threadEl);

  return new XMLSerializer().serializeToString(doc);
}

/** Read Thread provenance metadata from an SVG string. */
export function readSvgMetadata(svg: string): ThreadMetadata | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svg, "image/svg+xml");

  const threadEl = doc.querySelector("metadata thread-provenance");
  if (!threadEl) return null;

  const dict: Record<string, string> = {};
  for (const child of Array.from(threadEl.children)) {
    // "thread-version" -> "thread:version"
    const tag = child.tagName.replace("-", ":");
    dict[tag] = child.textContent ?? "";
  }

  if (!dict["thread:version"]) return null;
  return dictToMeta(dict);
}

// ---------------------------------------------------------------------------
// HTML
// ---------------------------------------------------------------------------

function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Embed Thread provenance metadata into an HTML string. */
export function embedHtmlMetadata(html: string, meta: ThreadMetadata): string {
  // Remove old thread meta tags
  let result = html.replace(
    /<meta\s+name="thread:[^"]*"\s+content="[^"]*"\s*\/?>\n?/g,
    "",
  );
  // Remove old thread comment
  result = result.replace(/<!-- thread:metadata \{.*?\} -->\n?/g, "");

  const dict = metaToDict(meta);
  const metaTags = Object.entries(dict)
    .map(([k, v]) => `<meta name="${k}" content="${escapeHtmlAttribute(v)}" />`)
    .join("\n");
  const comment = `<!-- thread:metadata ${JSON.stringify(dict)} -->`;

  const insertion = `${metaTags}\n${comment}\n`;

  if (result.includes("</head>")) {
    return result.replace("</head>", `${insertion}</head>`);
  }
  if (result.includes("<body")) {
    const idx = result.indexOf("<body");
    return result.slice(0, idx) + insertion + result.slice(idx);
  }
  return insertion + result;
}

/** Read Thread provenance metadata from an HTML string. */
export function readHtmlMetadata(html: string): ThreadMetadata | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const dict: Record<string, string> = {};
  const metaEls = doc.querySelectorAll('meta[name^="thread:"]');
  for (const el of Array.from(metaEls)) {
    const name = el.getAttribute("name") ?? "";
    const content = el.getAttribute("content") ?? "";
    dict[name] = content;
  }

  if (dict["thread:version"]) {
    return dictToMeta(dict);
  }

  // Fallback: parse the HTML comment
  const commentMatch = html.match(
    /<!-- thread:metadata (\{.*?\}) -->/,
  );
  if (commentMatch?.[1]) {
    try {
      const parsed = JSON.parse(commentMatch[1]) as Record<string, string>;
      if (parsed["thread:version"]) {
        return dictToMeta(parsed);
      }
    } catch {
      // Ignore malformed JSON
    }
  }

  return null;
}
